import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { api } from '../lib/api/client';
import type { AplicadaEmMassa, PreviaEmMassa } from '../lib/api/types';
import { useAuth } from '../lib/auth/AuthContext';
import { PageHeader } from '../components/ui/PageHeader';

/**
 * Marcação em massa (A17).
 *
 * <h3>A prévia não é conveniência, é a tela</h3>
 * O critério é uma chave de NF-e, uma sessão ou um lote — e a diferença entre marcar o
 * embarque certo e marcar a produção inteira do ano cabe num dígito digitado errado. A
 * prévia diz <b>quantos itens o critério alcança</b> antes de qualquer coisa acontecer, e o
 * botão de aplicar só existe depois dela.
 *
 * <p>A prévia é recalculada a cada mudança do critério. Uma contagem velha ao lado de um
 * critério novo é pior do que nenhuma contagem: ela dá confiança sobre o alvo errado.
 */
export function MarcacaoEmMassaPage() {
  const { traduzir, vocabulario } = useAuth();

  const [criterio, setCriterio] = useState('NFE');
  const [valor, setValor] = useState('');
  const [marcacao, setMarcacao] = useState('RECALLED');
  const [motivo, setMotivo] = useState('');
  const [revogar, setRevogar] = useState(false);
  const [previa, setPrevia] = useState<PreviaEmMassa | null>(null);
  const [aplicada, setAplicada] = useState<AplicadaEmMassa | null>(null);

  const verPrevia = useMutation({
    mutationFn: () =>
      api.post<PreviaEmMassa>('/console/markings/bulk/preview', { criterion: criterio, value: valor }),
    onSuccess: (p) => {
      setPrevia(p);
      setAplicada(null);
    },
  });

  const aplicar = useMutation({
    mutationFn: () =>
      api.post<AplicadaEmMassa>('/console/markings/bulk', {
        criterion: criterio,
        value: valor,
        marking: marcacao,
        reason: motivo,
        revokeCertificates: revogar && (previa?.certificadosAtivos ?? 0) > 0,
      }),
    onSuccess: (a) => {
      setAplicada(a);
      setPrevia(null);
      setRevogar(false);
    },
  });

  /** Mudar o alvo invalida a contagem. Sempre. */
  function mudouOAlvo(f: () => void) {
    f();
    setPrevia(null);
    setAplicada(null);
  }

  return (
    <>
      <PageHeader
        titulo="Marcação em massa"
        subtitulo="Recolher um embarque, bloquear um lote. Sempre com a contagem antes."
      />
      <div className="wrap" style={{ maxWidth: 720 }}>
        <div className="card cd">
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              verPrevia.mutate();
            }}
          >
            <div className="linha">
              <div className="campo">
                <label htmlFor="crit">Critério</label>
                <select
                  id="crit"
                  value={criterio}
                  onChange={(e) => mudouOAlvo(() => setCriterio(e.target.value))}
                >
                  <option value="NFE">Chave da NF-e</option>
                  <option value="SESSION">Sessão de captura (código)</option>
                  <option value="BATCH">Lote (código)</option>
                </select>
              </div>
              <div className="campo">
                <label htmlFor="val">Valor</label>
                <input
                  id="val"
                  value={valor}
                  onChange={(e) => mudouOAlvo(() => setValor(e.target.value))}
                  required
                />
              </div>
            </div>
            {verPrevia.isError && <div className="aviso ruim">{String(verPrevia.error)}</div>}
            <button className="btn btn-secondary" type="submit" disabled={verPrevia.isPending}>
              Ver quantos itens isto alcança
            </button>
          </form>
        </div>

        {previa && (
          <div className="card cd" style={{ marginTop: 16 }}>
            <div className="aviso">
              Este critério alcança <strong>{previa.itensAlcancados} item(ns)</strong>
              {previa.certificadosAtivos > 0 && (
                <>
                  , com <strong>{previa.certificadosAtivos} certificado(s) ativo(s)</strong>
                </>
              )}
              . Confira o número antes de aplicar — não há desfazer em massa.
            </div>
            <form
              className="form"
              style={{ marginTop: 16 }}
              onSubmit={(e) => {
                e.preventDefault();
                aplicar.mutate();
              }}
            >
              <div className="campo">
                <label htmlFor="mm">Marcar como</label>
                <select id="mm" value={marcacao} onChange={(e) => setMarcacao(e.target.value)}>
                  {Object.keys(vocabulario.marking ?? {}).map((c) => (
                    <option key={c} value={c}>
                      {traduzir('marking', c)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="campo">
                <label htmlFor="mot">Motivo</label>
                <input
                  id="mot"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  required
                />
                <span className="dica">
                  Entra na trilha de cada item alcançado, e é o que explica a decisão depois.
                </span>
              </div>
              {previa.certificadosAtivos > 0 && (
                <div className="campo">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={revogar}
                      onChange={(e) => setRevogar(e.target.checked)}
                    />{' '}
                    Revogar também os {previa.certificadosAtivos} certificado(s) ativo(s)
                  </label>
                  <span className="dica">
                    Com o mesmo motivo (de 10 a 300 caracteres). A página de cada certificado passa
                    a dizer "revogado" e o PDF deixa de sair. <strong>Remover a marcação depois
                    não devolve os certificados.</strong> Num recall a peça continua original: a
                    marca pode preferir manter o certificado, que já mostra o aviso no topo.
                  </span>
                </div>
              )}
              {aplicar.isError && <div className="aviso ruim">{String(aplicar.error)}</div>}
              <button
                className="btn btn-danger"
                type="submit"
                disabled={aplicar.isPending || previa.itensAlcancados === 0}
              >
                Marcar {previa.itensAlcancados} item(ns)
                {revogar && previa.certificadosAtivos > 0
                  ? ` e revogar ${previa.certificadosAtivos} certificado(s)`
                  : ''}
              </button>
            </form>
          </div>
        )}

        {aplicada && (
          <div className="aviso bom" style={{ marginTop: 16 }}>
            {aplicada.marcados} item(ns) marcado(s) como {traduzir('marking', aplicada.marcacao)}.
            {aplicada.certificadosRevogados > 0 &&
              ` ${aplicada.certificadosRevogados} certificado(s) revogado(s).`}{' '}
            O passaporte público de cada um já mostra o aviso.
          </div>
        )}
      </div>
    </>
  );
}
